<!-- eslint-disable vue/valid-template-root -->
<template>
  <component
    :is="session.features.promptTheFilter ? FilterModalV2 : FilterModal"
    v-if="hasWorkspaceConnections"
    :show-modal="!!showModal"
    :show-no-filters-modal="!allFilters.length && filtersInitialized"
    :public="showModal === 'public'"
    :editable-filter="editableFilter"
    can-convert-to-assignment
    @update="filtersChanged"
    @close="close"
    @stop-ticket-list-loading="ticketListLoading = false"
  />
</template>
<!-- eslint-enable vue/valid-template-root -->

<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import i18n from '@/i18n'
import {
  publicFilters,
  privateFilters,
  allFilters,
  filtersInitialized,
  activeFilterId,
  setActiveFilterId,
  getFilters,
  FilterType,
} from '@/modules/conversations/composables/useConversationFilters'
import removeFilter from '@/modules/conversations/components/filters/removeFilter'
import cloneFilter from '@/modules/conversations/components/filters/cloneFilter'
import FilterModal from '@/modules/conversations/components/filters/FilterModal.vue'
import FilterModalV2 from '@/modules/conversations/components/filters/FilterModalV2.vue'
import { hasWorkspaceRole, hasAccountRole } from '@/utils/roleUtils'
import useReviewDraft from '@/composables/useReviewDraft'
import { track } from '@/utils/analytics'
import { session } from '@/composables/useSession'
import { bus } from '@/utils/bus'
import useConversationList from '@/modules/conversations/composables/useConversationList'
import { useWorkspaceConnections } from '@/modules/workspace/composables/useWorkspaceConnections'
import useFilterModal from '../../conversations/composables/useFilterModal'
import type { SidebarConfig } from '../types'

type PublicFiltersType = typeof publicFilters.value

const emit = defineEmits<{
  'update:config': [config: SidebarConfig]
}>()

const { showModal, editableFilter, openFilterModal, editFilter, close } = useFilterModal()
const { drafts } = useReviewDraft()
const { ticketListLoading } = useConversationList()
const route = useRoute()
const { hasConnections: hasWorkspaceConnections, isFetched: workspaceConnectionsFetched } = useWorkspaceConnections(
  session.workspace.id,
)

const canManageFilters = (myFilter: boolean) =>
  myFilter || hasAccountRole('ADMIN', 'MANAGER') || hasWorkspaceRole('MANAGER')

const getDraftsCount = () => {
  if (!drafts.value.length) return

  return drafts.value.length > 99 ? '99+' : drafts.value.length
}

const handleGroupActionClick = (navItem: string, key: string) => {
  if (navItem !== 'conversations' || !hasWorkspaceConnections.value) return
  if (key === 'private_filters') {
    openFilterModal('private')
  }
  if (key === 'public_filters') {
    openFilterModal('public')
  }
}

const handleSubnavItemClick = (navItem: string, subnavItem: string | number) => {
  if (navItem === 'conversations') {
    if (subnavItem === 'drafts') {
      setActiveFilterId(FilterType.Drafts)
      track('Drafts opened')
    } else if (subnavItem === 'starred') {
      setActiveFilterId(FilterType.Starred)
      track('Starred opened')
    } else {
      // Handle filter selection - subnavItem could be a number (filterId) or string
      const allFilters = [...publicFilters.value, ...privateFilters.value]
      const filter = allFilters.find(
        (f) => f.filterId === subnavItem || f.filterId.toString() === subnavItem.toString(),
      )

      if (filter) setActiveFilterId(filter.filterId)
    }
  }
}

const handleSubnavItemActionClick = (navItem: string, _subnavItem: string | number, action: string) => {
  if (navItem === 'conversations') {
    const filter = [...publicFilters.value, ...privateFilters.value].find((f) => f.filterId === _subnavItem)
    if (!filter) return

    if (action === 'edit') {
      editFilter(filter?.isPublic ? 'public' : 'private', filter)
    }
    if (action === 'clone') {
      cloneFilter(filter)
    }
    if (action === 'delete') {
      removeFilter(filter.filterId, filter.name, filter.isPublic)
    }
  }
}

const toSidenavItem = (filters: PublicFiltersType) => {
  return filters.map((filter) => ({
    key: filter.filterId,
    children: filter.name,
    overflowMenu: true,
    canManageFilters: canManageFilters(filter.myFilter) && hasWorkspaceConnections.value,
    actions: canManageFilters(filter.myFilter)
      ? [
          ...(hasWorkspaceConnections.value
            ? [
                { key: 'edit', tooltip: i18n.t('conversations.filters.edit_filter') },
                { key: 'clone', tooltip: i18n.t('conversations.filters.clone_filter') },
              ]
            : []),
          { key: 'delete', tooltip: i18n.t('conversations.filters.delete_filter') },
        ]
      : [],
    isCurrent: activeFilterId.value === filter.filterId,
    dataTestId: `filter-${filter.filterId}`,
    view: 'conversations',
  }))
}

const items = computed(() => {
  const baseItems = []

  // Starred
  baseItems.push({
    children: i18n.t('conversations.sub_nav.starred'),
    key: 'starred',
    icon: 'star',
    dataTestId: 'starred',
    isCurrent: activeFilterId.value === FilterType.Starred,
    view: 'conversations',
  })

  // Drafts
  baseItems.push({
    children: i18n.t('conversations.sub_nav.drafts'),
    key: 'drafts',
    icon: 'note-pencil',
    dataTestId: 'drafts',
    badge: getDraftsCount(),
    isCurrent: activeFilterId.value === FilterType.Drafts,
    view: 'conversations',
  })

  const conditionalItems = []

  // Public filters
  if (!hasWorkspaceRole('AGENT')) {
    conditionalItems.push({
      action: {
        tooltip: hasWorkspaceConnections.value
          ? i18n.t('conversations.filters.add_public_filter')
          : i18n.t('conversations.filters.add_connection_first'),
      },
      key: 'public_filters',
      isCollapsible: true,
      dataTestId: 'public-filters',
      items: toSidenavItem(publicFilters.value),
      legend: i18n.t('conversations.filters.public_filters'),
      view: 'conversations',
      disabled: !hasWorkspaceConnections.value,
      emptyStateLabel: i18n.t('conversations.filters.empty_public_filter'),
    })
  }

  // Private filters
  conditionalItems.push({
    action: {
      tooltip: hasWorkspaceConnections.value
        ? i18n.t('conversations.filters.add_private_filter')
        : i18n.t('conversations.filters.add_connection_first'),
    },
    key: 'private_filters',
    isCollapsible: true,
    dataTestId: 'private-filters',
    items: toSidenavItem(privateFilters.value),
    legend: i18n.t('conversations.filters.private_filters'),
    view: 'conversations',
    disabled: !hasWorkspaceConnections.value,
    emptyStateLabel: i18n.t('conversations.filters.empty_private_filter'),
  })

  return [...baseItems, ...conditionalItems]
})

const config = computed(
  (): SidebarConfig => ({
    action: {
      tooltip: '',
      menuItems: [],
      onClick: () => {
        /* no-op */
      },
    },
    title: i18n.t('conversations.sub_nav.heading'),
    items: items.value,
    hideToggleSubnavButton: route.query?.singleTicket === 'true',
    handleGroupActionClick,
    handleSubnavItemClick,
    handleSubnavItemActionClick,
  }),
)

const filtersChanged = async () => {
  await getFilters()
  bus.$emit('refresh-active-filter')
}

const emitConfig = () => {
  emit('update:config', config.value)
}

const handleMissingWorkspaceConnections = () => {
  if (!filtersInitialized.value || !workspaceConnectionsFetched.value || hasWorkspaceConnections.value) return

  ticketListLoading.value = false

  if (!activeFilterId.value) {
    setActiveFilterId(FilterType.Starred)
    filtersChanged()
    track('Starred opened')
  }
}

onMounted(async () => {
  emitConfig()

  await getFilters()
  handleMissingWorkspaceConnections()
  emitConfig()
})

// Watch for changes and emit updates
watch(config, (newConfig) => {
  emit('update:config', newConfig)
})

watch([filtersInitialized, workspaceConnectionsFetched, hasWorkspaceConnections], handleMissingWorkspaceConnections)
</script>
